/*
  Trade-in form (2026-10-27). Markup: snippets/wolf-trade-in.liquid. Styles: assets/wolf-trade-in.css.
  Steps 1-5 move on by themselves after a tap. A step that already has an answer when it opens (after Back,
  or when the sheet is opened again) shows its Next button; keyboard users get it on focus everywhere.
  The form always opens at step 1. Answers are not saved between visits: resuming at a later step confused
  people (Omar, 2026-10-27: "it takes me straight to step five").
  On the product page the form sits in a <dialog>; [data-ti-open] buttons open it with the viewed product chosen.
*/
(() => {
  if (customElements.get('wolf-trade-in')) return;

  const AUTO_DELAY = 180;
  /* brand, model, storage (added 2026-10-28), battery, colour, the phone they want; then the review screen */
  const QUESTIONS = 6;
  const REVIEW = QUESTIONS + 1;

  /* Answers saved by the first version (2026-10-27) are no longer read; clear them from testers' phones. */
  try {
    localStorage.removeItem('wolf-tradein');
  } catch (e) {
    /* storage blocked: nothing to clear */
  }

  const TEXT = {
    ar: {
      step: (n) => `الخطوة ${n} من ${QUESTIONS}`,
      last: 'آخر خطوة',
      none: 'ما قررت بعد',
      join: '، ',
      message: (a) => [
        'السلام عليكم، أبي أبدل جهازي',
        `جهازي الحالي: ${a.model}`,
        `السعة: ${a.storage}`,
        `البطارية: ${a.battery}`,
        `اللون: ${a.color}`,
        a.target ? `أبي آخذ بدله: ${a.target}` : 'ما قررت أي جهاز آخذ بدله',
        a.url,
        'بأرسل صور الجهاز الحين',
      ],
    },
    en: {
      step: (n) => `Step ${n} of ${QUESTIONS}`,
      last: 'Last step',
      none: 'I haven’t decided yet',
      join: ', ',
      message: (a) => [
        'Hello, I’d like to trade in my phone',
        `My phone: ${a.model}`,
        `Storage: ${a.storage}`,
        `Battery health: ${a.battery}`,
        `Colour: ${a.color}`,
        a.target ? `I’d like to get: ${a.target}` : 'I haven’t decided which phone to get yet',
        a.url,
        'I’ll send photos of the phone now',
      ],
    },
  };

  const track = (name, data) => {
    try {
      window.Shopify?.analytics?.publish?.(name, data);
    } catch (e) {
      /* tracking must never break the form */
    }
  };

  class WolfTradeIn extends HTMLElement {
    connectedCallback() {
      if (this.form) return;
      this.form = this.querySelector('[data-ti-form]');
      this.t = TEXT[this.dataset.lang === 'en' ? 'en' : 'ar'];
      this.steps = [...this.querySelectorAll('[data-ti-step]')];
      this.step = 1;
      this.pointerAt = 0;

      this.form.addEventListener('submit', (e) => e.preventDefault());
      this.form.addEventListener('pointerdown', () => (this.pointerAt = Date.now()));
      this.form.addEventListener('change', (e) => this.onChange(e));
      this.form.addEventListener('input', (e) => {
        if (e.target.name === 'model_other') this.refresh();
      });
      this.addEventListener('click', (e) => this.onClick(e));

      this.showBrand();
      this.showTargetOptions();
      this.querySelectorAll('[data-ti-product]').forEach((card) => this.updateStock(card));
      if (this.dataset.mode === 'page') {
        this.go(1, { scroll: false, focus: false });
        track('wolf_tradein_view', { mode: 'page' });
      }
    }

    /* Called by the product page button before the dialog opens. Answers from this visit stay selected. */
    open() {
      this.prefill();
      this.go(1, { scroll: false, focus: false });
      track('wolf_tradein_view', { mode: 'sheet' });
    }

    checked(name) {
      return this.form.querySelector(`input[name="${CSS.escape(name)}"]:checked`);
    }

    label(name) {
      const input = this.checked(name);
      return input ? input.dataset.label || input.value : '';
    }

    uncheck(name) {
      this.form.querySelectorAll(`input[name="${CSS.escape(name)}"]`).forEach((i) => (i.checked = false));
    }

    otherText() {
      return (this.form.elements.model_other?.value || '').trim();
    }

    modelText() {
      const input = this.checked('model');
      if (!input) return '';
      return input.value === '__other' ? this.otherText() : input.dataset.label;
    }

    targetCard() {
      const input = this.checked('target');
      if (!input || input.value === 'none') return null;
      return this.querySelector(`[data-ti-product="${input.value}"]`);
    }

    variantsOf(card) {
      if (!card.tiVariants) card.tiVariants = JSON.parse(card.dataset.variants || '[]');
      return card.tiVariants;
    }

    /*
      Blocks sold-out choices (Omar, 2026-10-28). Storage comes first: a storage is blocked when no colour is in stock
      in it, and colours are blocked by what is in stock in the chosen storage. Any package counts. A blocked choice
      that was selected gets cleared, so the step cannot go on with it.
    */
    updateStock(card) {
      const id = card.dataset.tiProduct;
      const s = Number(card.dataset.s);
      const c = Number(card.dataset.c);
      const inStock = this.variantsOf(card).filter((v) => v.a);
      const set = (input, available) => {
        input.disabled = !available;
        if (!available && input.checked) input.checked = false;
      };
      if (s >= 0) {
        this.form.querySelectorAll(`input[name="ti-s-${id}"]`).forEach((i) => set(i, inStock.some((v) => v.o[s] === i.value)));
      }
      const storage = s >= 0 ? this.checked(`ti-s-${id}`)?.value : null;
      if (c >= 0) {
        this.form.querySelectorAll(`input[name="ti-c-${id}"]`).forEach((i) =>
          set(i, inStock.some((v) => v.o[c] === i.value && (!storage || v.o[s] === storage)))
        );
      }
    }

    targetParts(card) {
      const id = card.dataset.tiProduct;
      return {
        storage: Number(card.dataset.s) >= 0 ? this.checked(`ti-s-${id}`) : null,
        color: Number(card.dataset.c) >= 0 ? this.checked(`ti-c-${id}`) : null,
        needsStorage: Number(card.dataset.s) >= 0,
        needsColor: Number(card.dataset.c) >= 0,
      };
    }

    isValid(n) {
      switch (n) {
        case 1:
          return Boolean(this.checked('brand'));
        case 2:
          return Boolean(this.modelText());
        case 3:
          return Boolean(this.checked('storage'));
        case 4:
          return Boolean(this.checked('battery'));
        case 5:
          return Boolean(this.checked('color'));
        case 6: {
          const input = this.checked('target');
          if (!input) return false;
          if (input.value === 'none') return true;
          const p = this.targetParts(this.targetCard());
          return (!p.needsStorage || Boolean(p.storage)) && (!p.needsColor || Boolean(p.color));
        }
        default:
          return true;
      }
    }

    onChange(e) {
      const input = e.target;
      if (input.type !== 'radio') return;

      if (input.name === 'brand') {
        this.uncheck('model');
        this.querySelector('[data-ti-other]').hidden = true;
        this.showBrand();
      }
      if (input.name === 'model') {
        const other = input.value === '__other';
        this.querySelector('[data-ti-other]').hidden = !other;
        if (other) this.form.elements.model_other.focus();
      }
      if (input.name === 'target') this.showTargetOptions();
      if (input.name.startsWith('ti-s-')) this.updateStock(input.closest('[data-ti-product]'));

      this.refresh();

      /* Only a tap or click moves on by itself; arrow keys also fire change and must not skip steps. */
      if (input.hasAttribute('data-auto') && Date.now() - this.pointerAt < 1500) {
        clearTimeout(this.autoTimer);
        const from = this.step;
        this.autoTimer = setTimeout(() => {
          if (this.step === from) this.go(from + 1);
        }, AUTO_DELAY);
      }
    }

    onClick(e) {
      const el = e.target.closest('button, a');
      if (!el || !this.contains(el)) return;

      if (el.hasAttribute('data-ti-back')) this.go(this.step - 1);
      else if (el.hasAttribute('data-ti-next')) {
        if (this.isValid(this.step)) this.go(this.step + 1);
      } else if (el.hasAttribute('data-ti-goto')) this.go(Number(el.dataset.tiGoto));
      else if (el.hasAttribute('data-ti-show-all')) this.showAllTargets();
      else if (el.hasAttribute('data-ti-close')) this.closest('dialog')?.close();
      else if (el.hasAttribute('data-ti-send')) {
        this.buildMessage();
        track('wolf_tradein_whatsapp', { mode: this.dataset.mode, target: this.targetCard()?.dataset.title || 'none' });
      }
    }

    go(n, { scroll = true, focus = true } = {}) {
      clearTimeout(this.autoTimer);
      this.step = Math.min(Math.max(n, 1), REVIEW);
      this.dataset.step = String(this.step);
      this.steps.forEach((s) => {
        const current = Number(s.dataset.tiStep) === this.step;
        s.hidden = !current;
        /* Answered on arrival (Back, or the sheet opened again): show Next so the same answer can be kept. */
        if (current) s.classList.toggle('is-answered', this.isValid(this.step));
      });
      this.querySelector('[data-ti-back]').hidden = this.step === 1;
      this.querySelector('[data-ti-count]').textContent = this.step <= QUESTIONS ? this.t.step(this.step) : this.t.last;
      if (this.step === REVIEW) this.buildMessage();
      this.refresh();
      track('wolf_tradein_step', { step: this.step, mode: this.dataset.mode });

      if (scroll) {
        const dialog = this.closest('dialog');
        if (dialog) dialog.scrollTop = 0;
        else if (this.getBoundingClientRect().top < 0) this.scrollIntoView({ block: 'start' });
      }
      if (focus) this.steps.find((s) => !s.hidden)?.focus({ preventScroll: true });
    }

    refresh() {
      this.steps.forEach((s) => {
        const ok = this.isValid(Number(s.dataset.tiStep));
        s.querySelectorAll('[data-ti-next]').forEach((b) => (b.disabled = !ok));
      });
    }

    showBrand() {
      const brand = this.checked('brand')?.value || 'apple';
      this.querySelectorAll('[data-ti-brand]').forEach((el) => (el.hidden = el.dataset.tiBrand !== brand));
    }

    showTargetOptions() {
      const chosen = this.checked('target')?.value;
      this.querySelectorAll('[data-ti-product]').forEach((card) => {
        card.querySelector('.wolf-ti__product-opts').hidden = card.dataset.tiProduct !== chosen;
      });
    }

    showAllTargets() {
      this.querySelectorAll('[data-ti-product], [data-ti-none]').forEach((el) => (el.hidden = false));
      this.querySelector('[data-ti-show-all]').hidden = true;
      this.querySelector('[data-ti-prefill-note]').hidden = true;
    }

    /*
      Product page: choose the viewed product and the storage and colour picked on the page, unless they are sold out
      (then the customer picks from what is in stock). A sold-out product is not preselected at all.
    */
    prefill() {
      const productId = this.dataset.prefill;
      const card = productId && this.querySelector(`[data-ti-product="${productId}"]`);
      const radio = card?.querySelector('input[name="target"]');
      if (!card || radio.disabled) return;

      radio.checked = true;
      const form = this.dataset.form && document.getElementById(this.dataset.form);
      const variantId = Number(form?.querySelector('[name="id"]')?.value);
      const variant = this.variantsOf(card).find((v) => v.id === variantId);
      const pick = (name, value) => {
        const input = [...this.form.querySelectorAll(`input[name="${name}"]`)].find((i) => i.value === value);
        if (input && !input.disabled) input.checked = true;
      };
      this.updateStock(card);
      if (variant && Number(card.dataset.s) >= 0) pick(`ti-s-${productId}`, variant.o[Number(card.dataset.s)]);
      this.updateStock(card);
      if (variant && Number(card.dataset.c) >= 0) pick(`ti-c-${productId}`, variant.o[Number(card.dataset.c)]);

      this.querySelectorAll('[data-ti-product], [data-ti-none]').forEach((el) => (el.hidden = el !== card));
      this.querySelector('[data-ti-show-all]').hidden = false;
      this.querySelector('[data-ti-prefill-note]').hidden = false;
      this.showTargetOptions();
      this.refresh();
    }

    buildMessage() {
      const card = this.targetCard();
      let target = '';
      let url = '';
      if (card) {
        const p = this.targetParts(card);
        /* Isolate each option so "256 GB" keeps its order inside an Arabic line, on the page and in WhatsApp. */
        const iso = (s) => (s ? `⁨${s}⁩` : '');
        target = [card.dataset.title, iso(p.storage?.dataset.label), iso(p.color?.dataset.label)].filter(Boolean).join(this.t.join);
        const variants = this.variantsOf(card);
        const fits = (v) =>
          (!p.storage || v.o[Number(card.dataset.s)] === p.storage.value) &&
          (!p.color || v.o[Number(card.dataset.c)] === p.color.value);
        /* Link to a variant in stock when there is one (another package of the same storage and colour may be sold out). */
        const match = variants.find((v) => v.a && fits(v)) || variants.find(fits) || variants[0];
        url = window.location.origin + card.dataset.url + (match ? `?variant=${match.id}` : '');
      }

      const answers = {
        model: this.modelText(),
        storage: this.label('storage'),
        battery: this.label('battery'),
        color: this.label('color'),
        target,
        url,
      };
      this.querySelector('[data-ti-sum="model"]').textContent = answers.model;
      this.querySelector('[data-ti-sum="storage"]').textContent = answers.storage;
      this.querySelector('[data-ti-sum="battery"]').textContent = answers.battery;
      this.querySelector('[data-ti-sum="color"]').textContent = answers.color;
      this.querySelector('[data-ti-sum="target"]').textContent = target || this.t.none;

      const text = this.t.message(answers).filter(Boolean).join('\n');
      this.querySelector('[data-ti-send]').href = `https://wa.me/${this.dataset.wa}?text=${encodeURIComponent(text)}`;
    }
  }

  customElements.define('wolf-trade-in', WolfTradeIn);

  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-ti-open]');
    if (trigger) {
      const dialog = document.getElementById(trigger.dataset.tiOpen);
      const form = dialog?.querySelector('wolf-trade-in');
      if (!dialog || !form || typeof dialog.showModal !== 'function') return;
      form.open();
      dialog.showModal();
      return;
    }
    /* A tap on the dimmed area around the sheet closes it. */
    if (e.target instanceof HTMLDialogElement && e.target.classList.contains('wolf-ti-dialog')) e.target.close();
  });
})();
